import { useCallback, useEffect, useState } from 'react';

import { useBrowserStorage } from '@aspendental/shared-utils/react';
import { useRouter } from 'next/router';

import { FACILITY_CODE_STORAGE_KEY, IUser } from '@/constants';
import {
	BffBrandType,
	BffFacility,
	BffFacilityFieldsFragment,
	FacilityBrand,
	NearbyFacilitiesList,
	NearbyFacility,
} from '@/types/generated';
import { FacilityFieldsFragment } from '@/graphql/__generated/sdk';
import { Maybe, TAGBrand } from '@/types';
import { getGraphHeaders } from '@/utils/getGraphHeaders';
import { getBffNearByFacilitiesByLocation } from '@/services/getBffNearByFacilitiesByLocation';
import { mapFacilities } from '@/utils/mapFacilitiesResults/mapFacilitiesResults';
import { cacheFn } from '@/utils/cacheFn/cacheFn';

import { getBffFacilityById } from './getBffFacilityById';

export type OfficeInfoType =
	| (FacilityFieldsFragment & {
			attributes?: BffFacilityFieldsFragment['attributes'];
			email?: string | null;
			specialtyServices?: BffFacilityFieldsFragment['specialtyServices'];
	  })
	| null;
interface INearbyFacilities {
	appName: string;
	appVersion: string;
	enterpriseGraphqlUrl: string;
	brand: TAGBrand;
	coords: {
		latitude: number;
		longitude: number;
	};
	withinRadius?: number;
}

async function getFacilityDetails(
	brand: TAGBrand,
	facilityCode: string,
	appName: string,
	appVersion: string,
	enterpriseGraphqlUrl: string
) {
	const facilityBrand = brandThemeToFacilityBrand(brand);

	const { getGraphQLClient } = await import(/* webpackChunkName:"getGraphQLClient" */ '../graphql/getGraphQLClient');
	const facilityInfoResp = await getGraphQLClient(enterpriseGraphqlUrl).facilityByCode(
		{
			code: facilityCode,
			brand: facilityBrand as FacilityBrand,
		},
		getGraphHeaders(appName, appVersion)
	);
	const facilityInfo = facilityInfoResp.facility as FacilityFieldsFragment;
	return facilityInfo;
}
// get 200 miles nearby facilities by default for now
export const getNearbyFacilities = cacheFn(async function getNearbyFacilities({
	appName,
	appVersion,
	enterpriseGraphqlUrl,
	brand,
	coords,
	withinRadius = 200,
}: INearbyFacilities): Promise<NearbyFacility[]> {
	const { latitude, longitude } = coords;
	if (!latitude || !longitude) {
		return Promise.reject(`Invalid coordinates of latitude: ${latitude} and longitude: ${longitude}`);
	}
	const facilityBrand = brandThemeToFacilityBrand(brand);
	const { getGraphQLClient } = await import(/* webpackChunkName:"getGraphQLClient" */ '../graphql/getGraphQLClient');
	const facilitiesResponse = await getGraphQLClient(enterpriseGraphqlUrl).nearbyFacilities(
		{
			filter: {
				latitude,
				longitude,
				withinRadius,
				brand: facilityBrand as FacilityBrand,
			},
		},
		getGraphHeaders(appName, appVersion)
	);
	const nearbyFacilities = facilitiesResponse.nearbyFacilities as NearbyFacilitiesList;

	return nearbyFacilities.items;
});

const brandThemeToFacilityBrand = (brand: TAGBrand) => {
	//TODO: swap brands from FacilityBrand (EntGQL) to BffBrandType as they get migrated to fully use BEFFE instead of EntGQL
	switch (brand) {
		case TAGBrand.WellNow:
		case TAGBrand.YouWellNow:
		case TAGBrand.LivWellNow:
			return FacilityBrand.WellNow;
		case TAGBrand.ClearChoice:
		case TAGBrand.LobbyClearChoice:
		case TAGBrand.ConsultClearChoice:
			return FacilityBrand.ClearChoice;
		case TAGBrand.TeamTAG:
		case TAGBrand.AspenDental:
			return FacilityBrand.AspenDental;
		case TAGBrand.Motto:
			return BffBrandType.Motto;
		case TAGBrand.Lovet:
			return BffBrandType.Lovet;
		case TAGBrand.Chapter:
			return BffBrandType.Chapter;
		default:
			return FacilityBrand.AspenDental;
	}
};

export default function useFacilities(
	appName: string,
	appVersion: string,
	brand: TAGBrand,
	enterpriseGraphqlUrl: string,
	bffGraphqlUrl: string,
	useFacilityBeffeDataSource: boolean,
	user?: Maybe<IUser>,
	facilityCode?: string,
	defaultToCdn?: boolean,
	transitionallyUseBeffe?: boolean,
	useSessionStorage?: boolean,
	shouldFetchNearbyFacility?: boolean,
	showSetOfficeButtonInGoogleMapsCards?: boolean,
	fetchAllLocationsOnEmptyResults?: boolean
) {
	const {
		storageValue: storedFacilityCode,
		setValue,
		removeItem,
	} = useBrowserStorage({
		key: FACILITY_CODE_STORAGE_KEY,
		storage: useSessionStorage ? globalThis.sessionStorage : globalThis.localStorage,
	});

	const storageFacilityCode = storedFacilityCode?.toString();

	const [officeInfo, setOfficeInfo] = useState<OfficeInfoType>(null);
	const [nearbyFacilities, setNearbyFacilities] = useState<BffFacility[] | undefined | null>(undefined);
	const [isWaitingForNearbyFacilities, setIsWaitingForNearbyFacilities] = useState(shouldFetchNearbyFacility);
	const [locationState, setLocationState] = useState<{
		nearByOfficeLocations: Array<NearbyFacility> | null;
		closestOfficeInfo: NearbyFacility | null;
		distance?: number | null;
		schedulingSystem?: string | null;
		user: IUser;
	}>({
		closestOfficeInfo: null,
		nearByOfficeLocations: null,
		distance: null,
		user: {
			hasIpBasedCoordinates: false,
			coordinates: {
				latitude: 0,
				longitude: 0,
			},
		},
	});
	const getNearbyFacilitiesByGeolocation = useCallback(
		async (officeInformation?: OfficeInfoType) => {
			if (!shouldFetchNearbyFacility) {
				return;
			}

			const handleError = (error: Error | GeolocationPositionError) => {
				console.error('Error occurred: ', error);
				removeItem();
				setNearbyFacilities(null);
			};

			type NearbyFacilityWithAttributes = NearbyFacility & {
				facility: FacilityFieldsFragment & {
					attributes?: BffFacilityFieldsFragment['attributes'];
					specialtyServices?: BffFacilityFieldsFragment['specialtyServices'];
				};
			};

			const handleSuccess = (res: NearbyFacilityWithAttributes[], nearbyFacilities: BffFacility[] | null) => {
				if (!res || res.length === 0 || res[0].facility === null) {
					removeItem();
					return false;
				}

				setLocationState((prev) => ({
					...prev,
					nearByOfficeLocations: res,
					closestOfficeInfo: res[0],
				}));
				setOfficeInfo(res[0].facility);
				setValue(res[0].facility.code);
				setNearbyFacilities(nearbyFacilities);

				return true;
			};

			const getPosition = (): Promise<GeolocationPosition> => {
				return new Promise((resolve, reject) => {
					globalThis.navigator?.geolocation.getCurrentPosition(resolve, reject, {
						timeout: 10000,
						maximumAge: 60000,
					});
				});
			};

			try {
				let position: { coords: { latitude: number; longitude: number } } | GeolocationPosition = {
					coords: { latitude: 0, longitude: 0 },
				};
				if (officeInformation?.location?.longitude && officeInformation?.location?.latitude) {
					position = {
						coords: {
							latitude: officeInformation?.location?.latitude as number,
							longitude: officeInformation?.location?.longitude as number,
						},
					};
				}

				if (defaultToCdn && !officeInformation) {
					position = {
						coords: {
							latitude: (user?.coordinates.latitude as number) || 0,
							longitude: (user?.coordinates.longitude as number) || 0,
						},
					};
				}

				if (!defaultToCdn && !officeInformation) {
					position = await getPosition();
				}

				let facilities: NearbyFacility[] = [];
				let nearbyFacilities: BffFacility[] | null = null;

				if (useFacilityBeffeDataSource) {
					const res = await getBffNearByFacilitiesByLocation(
						brandThemeToFacilityBrand(brand).toString() as BffBrandType,
						position?.coords.latitude || 0,
						position?.coords.longitude || 0,
						bffGraphqlUrl,
						appName,
						appVersion,
						{ fetchAllIfNoResults: fetchAllLocationsOnEmptyResults }
					);
					facilities = mapFacilities(res?.facilityByLocation);
					nearbyFacilities =
						res?.facilityByLocation?.filter((facility): facility is BffFacility => facility !== null) ??
						null;
				} else {
					facilities = await getNearbyFacilities({
						appName,
						appVersion,
						enterpriseGraphqlUrl,
						brand,
						coords: position?.coords || { longitude: 0, latitude: 0 },
					});
				}

				setLocationState((prev) => ({
					...prev,
					user: {
						hasIpBasedCoordinates: defaultToCdn ? user?.hasIpBasedCoordinates : false,
						coordinates: position?.coords || { longitude: 0, latitude: 0 },
					},
				}));
				handleSuccess(facilities, nearbyFacilities);
			} catch (error) {
				const geolocationError = error as GeolocationPositionError; // Type assertion

				if (
					(!defaultToCdn &&
						typeof geolocationError === 'object' &&
						'code' in geolocationError &&
						geolocationError.code === geolocationError.PERMISSION_DENIED &&
						user?.hasIpBasedCoordinates) ||
					(user?.coordinates && user?.hasIpBasedCoordinates)
				) {
					console.info('Browser Geolocation is not enabled, using IP based coordinates', user.coordinates);
					try {
						let facilities: NearbyFacility[] = [];
						let nearbyFacilities: BffFacility[] | null = null;

						if (useFacilityBeffeDataSource) {
							const res = await getBffNearByFacilitiesByLocation(
								brandThemeToFacilityBrand(brand)?.toString() as BffBrandType,
								user?.coordinates.latitude || 0,
								user?.coordinates.longitude || 0,
								bffGraphqlUrl,
								appName,
								appVersion,
								{ fetchAllIfNoResults: fetchAllLocationsOnEmptyResults }
							);
							facilities = mapFacilities(res?.facilityByLocation);
							nearbyFacilities =
								res?.facilityByLocation?.filter(
									(facility): facility is BffFacility => facility !== null
								) ?? null;
						} else {
							facilities = await getNearbyFacilities({
								appName,
								appVersion,
								enterpriseGraphqlUrl,
								brand,
								coords: user?.coordinates || { longitude: 0, latitude: 0 },
							});
						}

						setLocationState((prev) => ({
							...prev,
							user: {
								hasIpBasedCoordinates: true,
								coordinates: user?.coordinates || { longitude: 0, latitude: 0 },
							},
						}));
						handleSuccess(facilities, nearbyFacilities);
					} catch (err) {
						const error = err as Error;
						handleError(error);
					}
				} else {
					handleError(error as Error);
				}
			} finally {
				setIsWaitingForNearbyFacilities(false);
			}
		},
		[
			shouldFetchNearbyFacility,
			removeItem,
			setValue,
			defaultToCdn,
			useFacilityBeffeDataSource,
			user?.coordinates,
			user?.hasIpBasedCoordinates,
			brand,
			bffGraphqlUrl,
			appName,
			appVersion,
			fetchAllLocationsOnEmptyResults,
			enterpriseGraphqlUrl,
		]
	);

	const router = useRouter();
	const facilityCodeFromURL = router.isReady
		? new URL(`${globalThis?.location?.origin || 'http://localhost'}${router.asPath}`).searchParams.get('fc')
		: null;
	useEffect(() => {
		// 1. If facility code is passed in the URL, use that to fetch the facility details
		// 2. If 1. failed, and facility code passed from server side props, use that to fetch the facility details
		// 3. if 1. and 2. both failed, and facility code is stored in local storage, use that to fetch the facility details
		// Final fallback: use the geolocation to fetch the facility details
		// new set office btn is introduced in google maps based on feature flag
		// if enabled, set as office btn feature in google maps when used sets the local storage for that facility
		//

		setIsWaitingForNearbyFacilities(true);
		const tempFacilityCode = showSetOfficeButtonInGoogleMapsCards
			? // when showSetOfficeButtonInGoogleMapsCards is true use storageFacilityCode before facilityCode
			  facilityCodeFromURL ?? storageFacilityCode ?? facilityCode ?? ''
			: // when showSetOfficeButtonInGoogleMapsCards is false use facilityCode before storageFacilityCode
			  facilityCodeFromURL ?? facilityCode ?? storageFacilityCode ?? '';

		const getData = async () => {
			if (tempFacilityCode) {
				try {
					let facilityDetails: OfficeInfoType = null;
					if (useFacilityBeffeDataSource || transitionallyUseBeffe) {
						const data = await getBffFacilityById(
							brandThemeToFacilityBrand(brand).toString() as BffBrandType,
							tempFacilityCode,
							bffGraphqlUrl,
							appName,
							appVersion
						);
						const facilities = mapFacilities(data?.facilityById ? [data.facilityById] : null);
						facilityDetails = facilities[0]?.facility ?? null;
					} else {
						facilityDetails = await getFacilityDetails(
							brand,
							tempFacilityCode,
							appName,
							appVersion,
							enterpriseGraphqlUrl
						);
					}
					if (facilityDetails) {
						setValue(tempFacilityCode);
						setOfficeInfo(facilityDetails);
						setIsWaitingForNearbyFacilities(false);
						return true;
					}
				} catch (e) {
					console.error('Error fetching facility by code:', e);
				}
			}
			if (shouldFetchNearbyFacility) {
				await getNearbyFacilitiesByGeolocation();
			}
			setIsWaitingForNearbyFacilities(false);
		};
		void getData();
	}, [
		brand,
		appName,
		appVersion,
		getNearbyFacilitiesByGeolocation,
		storageFacilityCode,
		facilityCodeFromURL,
		setValue,
		enterpriseGraphqlUrl,
		removeItem,
		facilityCode,
		useFacilityBeffeDataSource,
		bffGraphqlUrl,
		transitionallyUseBeffe,
		shouldFetchNearbyFacility,
		setIsWaitingForNearbyFacilities,
		showSetOfficeButtonInGoogleMapsCards,
	]);

	useEffect(() => {
		if (officeInfo) {
			if (locationState.nearByOfficeLocations) {
				const isOfficeNearby = locationState.nearByOfficeLocations.some(
					(office) => office.facility.code === officeInfo.code
				);
				if (isOfficeNearby) {
					//TODO: update the distance
				}
				setIsWaitingForNearbyFacilities(false);
			} else {
				void getNearbyFacilitiesByGeolocation(officeInfo);
			}
		}
	}, [officeInfo, locationState, getNearbyFacilitiesByGeolocation, setIsWaitingForNearbyFacilities]);

	return {
		officeInfo,
		facilityCode: storageFacilityCode,
		locationState,
		facilityBrand: brandThemeToFacilityBrand(brand),
		nearbyFacilities,
		isWaitingForNearbyFacilities,
	};
}
